/**
 * useSalesUnitFilter Hook
 * Shared hook for sales unit data isolation across all modules.
 * Calls /my-sales-units once and caches the result.
 * Admin users bypass filtering automatically.
 */

import { useState, useEffect, useRef } from 'react';
import { salesService } from '@/modules/sales/services/salesService';

interface SalesUnitFilterResult {
  /** Sales unit IDs the current user has access to */
  salesUnitIds: number[];
  /** Whether the data is still loading */
  isLoading: boolean;
  /** Whether filtering should be applied (false for admins) */
  shouldFilter: boolean;
  /** Comma-separated salesUnitIds string for query params, or empty string if no filtering */
  salesUnitParam: string;
}

// Module-level cache so multiple components share the same result
let cachedResult: { salesUnitIds: number[]; isAdmin: boolean } | null = null;
let cachePromise: Promise<{ salesUnitIds: number[]; isAdmin: boolean }> | null = null;

async function fetchSalesUnits(): Promise<{ salesUnitIds: number[]; isAdmin: boolean }> {
  const isAdmin = salesService.isCurrentUserAdmin();
  if (isAdmin) {
    return { salesUnitIds: [], isAdmin: true };
  }

  const response = await salesService.getCurrentUserSalesUnits();
  return {
    salesUnitIds: response.success ? response.salesUnitIds : [],
    isAdmin: false,
  };
}

export function useSalesUnitFilter(): SalesUnitFilterResult {
  const [salesUnitIds, setSalesUnitIds] = useState<number[]>(cachedResult?.salesUnitIds ?? []);
  const [isAdmin, setIsAdmin] = useState(cachedResult?.isAdmin ?? false);
  const [isLoading, setIsLoading] = useState(cachedResult === null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    if (cachedResult) {
      setSalesUnitIds(cachedResult.salesUnitIds);
      setIsAdmin(cachedResult.isAdmin);
      setIsLoading(false);
      return;
    }

    // Deduplicate in-flight requests
    if (!cachePromise) {
      cachePromise = fetchSalesUnits();
    }

    cachePromise
      .then((result) => {
        cachedResult = result;
        if (mountedRef.current) {
          setSalesUnitIds(result.salesUnitIds);
          setIsAdmin(result.isAdmin);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          setIsLoading(false);
        }
      })
      .finally(() => {
        cachePromise = null;
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const shouldFilter = !isAdmin && salesUnitIds.length > 0;
  const salesUnitParam = shouldFilter ? salesUnitIds.join(',') : '';

  return { salesUnitIds, isLoading, shouldFilter, salesUnitParam };
}

/** Call this on logout to clear the cached sales units */
export function clearSalesUnitCache(): void {
  cachedResult = null;
  cachePromise = null;
}
